import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import classnames from 'classnames';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
import Button from 'choerodon-ui/pro/lib/button';
import { FuncType } from 'choerodon-ui/pro/lib/button/enum';
import { useModal } from 'choerodon-ui/pro/lib/modal-provider/ModalProvider';
import { iteratorReduce } from 'choerodon-ui/pro/lib/_util/iteratorUtils';
import Dropdown from 'choerodon-ui/pro/lib/dropdown';
import { $l } from 'choerodon-ui/pro/lib/locale-context';
import { getActiveKeyByGroupKey, getDataAttr, getHeader, getLeft, getTop, isTransformSupported, isVertical, getStyle, setTransform, } from './utils';
import warning from '../_util/warning';
import Ripple from '../ripple';
import TabBarInner from './TabBarInner';
import EventManager from '../_util/EventManager';
import { TabsType } from './enum';
import Icon from '../icon';
import Menu from '../menu';
import MenuItem from '../menu/MenuItem';
import Badge from '../badge';
import TabsContext from './TabsContext';
import KeyCode from '../_util/KeyCode';
import { Size } from '../_util/enum';
import CustomizationSettings from './customization-settings';
import Count from './Count';
import TabsAddBtn from './TabsAddBtn';
const TabBar = function TabBar(props) {
    const { scrollAnimated, className, style, inkBarStyle, extraContent, tabBarGutter, inkBarAnimated, type, showMore, hideAdd, onRemoveTab, onEdit, ...restProps } = props;
    const { keyboard, customizable, prefixCls, activeKey, activeGroupKey, tabBarPosition, hideOnlyGroup = false, groupedPanelsMap, currentPanelMap, onTabClick, onPrevClick = noop, onNextClick = noop, changeActiveKey, } = useContext(TabsContext);
    const modal = useModal();
    const openCustomizationModal = useCallback(() => {
        if (customizable) {
            const modalProps = {
                drawer: true,
                size: Size.small,
                title: $l('Tabs', 'customization_settings'),
                children: React.createElement(CustomizationSettings, null),
                bodyStyle: {
                    overflow: 'hidden auto',
                    padding: 0,
                },
            };
            modalProps.okText = $l('Tabs', 'save');
            modal.open(modalProps);
        }
    }, [customizable, modal]);
    const getNextActiveKey = useCallback((next) => {
        const list = [];
        currentPanelMap.forEach((c, key) => {
            if (!c.disabled) {
                if (next) {
                    list.push(key);
                }
                else {
                    list.unshift(key);
                }
            }
        });
        const { length } = list;
        if (activeKey && length) {
            const i = list.indexOf(activeKey);
            const itemIndex = i === length - 1 ? 0 : i + 1;
            return list[itemIndex] || list[0];
        }
        return undefined;
    }, [activeKey, currentPanelMap]);
    const handleKeyDown = useCallback(e => {
        if (keyboard === false) {
            return noop;
        }
        const { keyCode } = e;
        if (keyCode === KeyCode.RIGHT || keyCode === KeyCode.DOWN) {
            e.preventDefault();
            const nextKey = getNextActiveKey(true);
            if (nextKey) {
                changeActiveKey(nextKey);
            }
        }
        else if (keyCode === KeyCode.LEFT || keyCode === KeyCode.UP) {
            e.preventDefault();
            const previousKey = getNextActiveKey(false);
            if (previousKey) {
                changeActiveKey(previousKey);
            }
        }
    }, [keyboard, changeActiveKey, getNextActiveKey]);
    const handleTabClick = useCallback((key) => {
        if (onTabClick) {
            onTabClick(key);
        }
        changeActiveKey(key);
    }, [changeActiveKey, onTabClick]);
    const handleGroupSelect = useCallback((param) => {
        const { key } = param;
        if (activeGroupKey !== key) {
            const newActiveKey = getActiveKeyByGroupKey(groupedPanelsMap, key);
            if (newActiveKey) {
                changeActiveKey(newActiveKey, true);
            }
        }
    }, [changeActiveKey, activeGroupKey, groupedPanelsMap]);
    const getOffsetWH = useCallback((node) => {
        return node[isVertical(tabBarPosition) ? 'offsetHeight' : 'offsetWidth'];
    }, [tabBarPosition]);
    const getScrollWH = useCallback((node) => {
        return node[isVertical(tabBarPosition) ? 'scrollHeight' : 'scrollWidth'];
    }, [tabBarPosition]);
    const getOffsetLT = useCallback((node) => {
        return node.getBoundingClientRect()[isVertical(tabBarPosition) ? 'top' : 'left'];
    }, [tabBarPosition]);
    const resizeEvent = useMemo(() => new EventManager(typeof window === 'undefined' ? undefined : window), []);
    const lastNextPrevShownRef = useRef();
    const offsetRef = useRef(0);
    const containerRef = useRef(null);
    const navWrapRef = useRef(null);
    const navScrollRef = useRef(null);
    const navRef = useRef(null);
    const rootRef = useRef(null);
    const activeTabRef = useRef(null);
    const inkBarRef = useRef(null);
    const [next, setNext] = useState(false);
    const [prev, setPrev] = useState(false);
    const [prevActiveKey, setActiveKey] = useState(activeKey);
    const [menuList, setMenuList] = useState([]);
    const tabsRef = useRef([]);
    const getTabs = () => {
        const length = currentPanelMap.size;
        const tabBarRef = [...currentPanelMap.entries()].map((item) => ({ key: item[0], value: item[1], ref: React.createRef() }));
        tabsRef.current = tabBarRef;
        const isSecond = type === TabsType['second-level'];
        return iteratorReduce(currentPanelMap.entries(), (rst, [key, child], index) => {
            const { disabled, closable = true, showCount, count, overflowCount, countRenderer } = child;
            const classes = isSecond ? [`${prefixCls}-second-tab`] : [`${prefixCls}-tab`];
            const tabProps = {
                tabKey: key,
                role: 'tab',
                'aria-disabled': 'false',
                'aria-selected': 'false',
                style: {
                    marginRight: tabBarGutter && index === length - 1 ? 0 : tabBarGutter,
                },
            };
            if (disabled) {
                classes.push(`${classes}-disabled`);
                tabProps['aria-disabled'] = 'true';
            }
            else {
                tabProps.onTabClick = handleTabClick;
            }
            if (activeKey === key) {
                classes.push(isSecond ? `${classes}-active` : `${classes}-active`);
                tabBarRef[index].ref = activeTabRef;
                tabProps['aria-selected'] = 'true';
            }
            tabProps.className = classes.join(' ');
            warning('tab' in child || 'title' in child, 'There must be `tab` or `title` property on children of Tabs.');
            const title = (React.createElement(React.Fragment, null,
                getHeader(child),
                showCount && (React.createElement(Count, { prefixCls: prefixCls, count: count, renderer: countRenderer, overflowCount: overflowCount, asyncCount: renderInkBar }))));
            rst.push(React.createElement(Ripple, { disabled: disabled, key: key },
                React.createElement(TabBarInner, Object.assign({ ref: tabBarRef[index].ref }, tabProps), type === TabsType['editable-card'] ? (React.createElement("div", { className: closable ? undefined : `${prefixCls}-tab-unclosable` },
                    title,
                    closable && React.createElement(Icon, { type: "close", onClick: e => onRemoveTab(key, e) }))) : title)));
            return rst;
        }, []);
    };
    const isNextPrevShown = useCallback((state) => {
        if (state) {
            return state.next || state.prev;
        }
        return next || prev;
    }, [next, prev]);
    const onMenuClick = ({ key }) => {
        changeActiveKey(key);
        scrollToActiveTab();
    };
    const menu = () => {
        return (React.createElement(Menu, { style: { maxHeight: 200, overflow: 'auto' }, onClick: onMenuClick }, menuList.map(x => (React.createElement(Menu.Item, { key: x.key },
            React.createElement("a", { target: "_blank" }, getHeader(x)))))));
    };
    const getContent = (contents) => {
        const vertical = isVertical(tabBarPosition);
        const isEditCard = type === TabsType['editable-card'];
        const classes = classnames(`${prefixCls}-extra-content`, {
            [`${prefixCls}-extra-vertical-content`]: vertical,
        });
        const dropDownClass = classnames(`${prefixCls}-more-tab`, {
            [`${prefixCls}-more-vertical-tab`]: vertical,
        });
        const nextPrevShown = isNextPrevShown();
        const moreTool = nextPrevShown && !!showMore && (React.createElement(Dropdown, { overlay: menu, key: "more" },
            React.createElement(Icon, { type: "more_horiz", className: dropDownClass })));
        const addTool = isEditCard && nextPrevShown && !hideAdd && (React.createElement(TabsAddBtn, { key: "add", onEdit: onEdit, vertical: vertical, isFixed: true }));
        // 这里是固定项
        const toolBar = [moreTool, addTool];
        if (extraContent || customizable) {
            return [
                contents,
                React.createElement("div", { key: "extra", className: classes },
                    customizable && (React.createElement(Button, { className: `${prefixCls}-hover-button`, funcType: FuncType.flat, icon: "predefine", size: Size.small, onClick: openCustomizationModal })),
                    toolBar,
                    extraContent),
            ];
        }
        return [
            contents,
            React.createElement("div", { key: "extra", className: classes }, toolBar),
        ];
    };
    const getGroupNode = () => {
        if (groupedPanelsMap.size > Number(hideOnlyGroup)) {
            const items = [];
            groupedPanelsMap.forEach((pane, key) => {
                const { group: { tab, disabled, dot } } = pane;
                items.push(React.createElement(MenuItem, { key: String(key), disabled: disabled },
                    React.createElement(Badge, { dot: dot }, tab)));
            });
            return (React.createElement(Menu, { prefixCls: `${prefixCls}-group`, selectedKeys: activeGroupKey ? [activeGroupKey] : [], onSelect: handleGroupSelect, mode: isVertical(tabBarPosition) ? 'vertical' : 'horizontal' }, items));
        }
        return undefined;
    };
    const getInkBarNode = () => {
        const inkBarClassName = `${prefixCls}-ink-bar`;
        const classes = classnames(inkBarClassName, inkBarAnimated ?
            `${inkBarClassName}-animated` :
            `${inkBarClassName}-no-animated`);
        return (React.createElement("div", { style: inkBarStyle, className: classes, key: "inkBar", ref: inkBarRef }));
    };
    const setOffset = useCallback((offset, callback) => {
        const nav = navRef.current; // 节点长度
        const navScroll = navScrollRef.current;
        if (nav && navScroll) {
            const target = Math.round(offset);
            if (offsetRef.current !== target) {
                offsetRef.current = Math.abs(target);
                const navOffset = {};
                const navStyle = nav.style;
                const transformSupported = isTransformSupported(navStyle);
                if (isVertical(tabBarPosition)) {
                    if (transformSupported) {
                        navScroll.scrollTo({ top: Math.abs(target) });
                    }
                    else {
                        navOffset.name = 'top';
                        navOffset.value = `${target}px`;
                    }
                }
                else if (transformSupported) {
                    navScroll.scrollTo({ left: Math.abs(target) });
                }
                else {
                    navOffset.name = 'left';
                    navOffset.value = `${target}px`;
                }
                if (transformSupported) {
                    setTransform(navStyle, navOffset.value);
                }
                else if (navOffset.name) {
                    navStyle[navOffset.name] = navOffset.value;
                }
                if (callback) {
                    callback();
                }
            }
        }
    }, [offsetRef, navRef, tabBarPosition]);
    const setNextPrev = useCallback(() => {
        const navNode = navRef.current;
        const container = containerRef.current;
        const navWrap = navWrapRef.current;
        if (navNode && container && navWrap) {
            const navNodeWH = getScrollWH(navNode);
            const containerWH = getOffsetWH(container);
            const navWrapNodeWH = getOffsetWH(navWrap);
            const offset = Math.round(offsetRef.current);
            // 当容器小于tab的时候使用最小值才可以防止回弹问题。
            const minOffset = Math.round(navNodeWH - navWrapNodeWH);
            let $next = next;
            let $prev = prev;
            if (offset === 0 && containerWH < navNodeWH) {
                $prev = false;
                $next = true;
            }
            else if (minOffset > 0 && offset === minOffset) {
                $prev = true;
                $next = false;
            }
            else if (offset > 0) {
                $prev = true;
                $next = true;
            }
            else {
                $prev = false;
                $next = false;
            }
            if (next !== $next) {
                setNext($next);
            }
            if (prev !== $prev) {
                setPrev($prev);
            }
            setOffset(offset);
            return {
                next: $next,
                prev: $prev,
            };
        }
    }, [next, prev, navRef, containerRef, navWrapRef, offsetRef, getScrollWH, getOffsetWH, setOffset]);
    const toPrev = useCallback(() => {
        const navWrapNode = navWrapRef.current;
        if (navWrapNode) {
            const navWrapNodeWH = getOffsetWH(navWrapNode);
            const offset = offsetRef.current - navWrapNodeWH;
            setOffset(offset < 0 ? 0 : 0 - offset, setNextPrev);
        }
    }, [getOffsetWH, setOffset, navWrapRef, onPrevClick, setNextPrev]);
    const toNext = useCallback(() => {
        const navWrapNode = navWrapRef.current;
        const navNode = navRef.current;
        if (navWrapNode && navNode) {
            const navNodeWH = getScrollWH(navNode);
            const navWrapNodeWH = getOffsetWH(navWrapNode);
            const offset = offsetRef.current + navWrapNodeWH;
            setOffset(0 - (offset > navNodeWH ? navNodeWH - navWrapNodeWH : offset), setNextPrev);
        }
    }, [getOffsetWH, setOffset, navWrapRef, onNextClick, setNextPrev]);
    const scrollToActiveTab = useCallback((e) => {
        const vertical = isVertical(tabBarPosition);
        const activeTab = activeTabRef.current;
        const navWrap = navWrapRef.current;
        if ((e && e.target !== e.currentTarget) || !activeTab || !navWrap) {
            return;
        }
        // when not scrollable or enter scrollable first time, don't emit scrolling
        const needToSroll = isNextPrevShown();
        if (!needToSroll) {
            return;
        }
        const activeTabWH = getOffsetWH(activeTab) + getStyle(activeTab, vertical ? 'margin-bottom' : 'margin-right');
        const navWrapNodeWH = getOffsetWH(navWrap);
        let offset = navScrollRef.current ? (vertical ? navScrollRef.current.scrollTop : navScrollRef.current.scrollLeft) : 0;
        const wrapOffset = getOffsetLT(navWrap);
        const activeTabOffset = getOffsetLT(activeTab);
        if (wrapOffset > activeTabOffset) {
            offset -= (wrapOffset - activeTabOffset);
            setOffset(offset, setNextPrev);
        }
        else if ((wrapOffset + navWrapNodeWH) < (activeTabOffset + activeTabWH)) {
            offset += (activeTabOffset + activeTabWH) - (navWrapNodeWH + wrapOffset);
            setOffset(offset, setNextPrev);
        }
    }, [activeTabRef, navWrapRef, lastNextPrevShownRef, getScrollWH, getOffsetWH, getOffsetLT, setOffset, setNextPrev, isNextPrevShown]);
    const prevTransitionEnd = useCallback(e => {
        if (e.propertyName !== 'opacity') {
            return;
        }
        const { current } = containerRef;
        if (current) {
            scrollToActiveTab({
                target: current,
                currentTarget: current,
            });
        }
    }, [scrollToActiveTab, containerRef]);
    const getScrollBarNode = (content) => {
        const showNextPrev = isNextPrevShown();
        const prevButton = (React.createElement("span", { onClick: prev ? toPrev : undefined, unselectable: "on", className: classnames(`${prefixCls}-tab-prev`, {
                [`${prefixCls}-tab-btn-disabled`]: !prev,
                [`${prefixCls}-tab-arrow-show`]: showNextPrev,
            }), onTransitionEnd: prevTransitionEnd },
            React.createElement("span", { className: `${prefixCls}-tab-prev-icon` })));
        const nextButton = (React.createElement("span", { onClick: next ? toNext : undefined, unselectable: "on", className: classnames({
                [`${prefixCls}-tab-next`]: 1,
                [`${prefixCls}-tab-btn-disabled`]: !next,
                [`${prefixCls}-tab-arrow-show`]: showNextPrev,
            }) },
            React.createElement("span", { className: `${prefixCls}-tab-next-icon` })));
        const navClassName = type === TabsType['second-level'] ? `${prefixCls}-second-nav` : `${prefixCls}-nav`;
        const navClasses = classnames(navClassName, scrollAnimated ? `${navClassName}-animated` : `${navClassName}-no-animated`);
        const vertical = isVertical(tabBarPosition);
        const isEditCard = type === TabsType['editable-card'];
        return (React.createElement("div", { className: classnames({
                [`${prefixCls}-nav-container`]: 1,
                [`${prefixCls}-nav-container-scrolling`]: showNextPrev && !showMore,
            }), key: "container", ref: containerRef },
            !showMore && prevButton,
            !showMore && nextButton,
            React.createElement("div", { className: `${prefixCls}-nav-wrap`, ref: navWrapRef },
                React.createElement("div", { className: `${prefixCls}-nav-scroll`, ref: navScrollRef },
                    React.createElement("div", { className: navClasses, ref: navRef },
                        content,
                        !hideAdd && isEditCard && (React.createElement(TabsAddBtn, { prefixCls: prefixCls, extraPrefixCls: showNextPrev ? 'none' : 'inline-flex', vertical: vertical, onEdit: onEdit })))))));
    };
    // 发生滚动事件，改变更多的菜单。
    const handleScrollEvent = useCallback(({ target }) => {
        const vertical = isVertical(tabBarPosition);
        const { scrollLeft, scrollTop } = target;
        let hiddenOffset = 0;
        const prevMenuList = [];
        // 计算前面隐藏的tabs
        for (let i = 0; i < tabsRef.current.length; i++) {
            const { key, value, ref } = tabsRef.current[i];
            const dom = ref.current;
            let currentTabOffset = 0;
            if (dom) {
                currentTabOffset = vertical ? dom.offsetHeight + getStyle(dom, 'margin-bottom') : dom.offsetWidth + getStyle(dom, 'margin-right');
            }
            hiddenOffset += currentTabOffset || 0;
            if ((!vertical && scrollLeft > 0) || (vertical && scrollTop > 0)) {
                prevMenuList.push({ key, ...value });
            }
            if ((!vertical && scrollLeft < hiddenOffset) || (vertical && scrollTop < hiddenOffset)) {
                break;
            }
        }
        // 计算后面隐藏的tabs
        const nextMenuList = [];
        const navNode = navRef.current; // 节点长度
        const navWrap = navWrapRef.current; // 节点滚动区域
        let totalHiddenOffset = 0;
        if (navNode && navWrap) {
            const navNodeWH = getScrollWH(navNode);
            const navWrapNodeWH = getOffsetWH(navWrap);
            totalHiddenOffset = navNodeWH - navWrapNodeWH - (vertical ? scrollTop : scrollLeft);
        }
        let hiddenBackOffset = 0;
        for (let i = tabsRef.current.length - 1; i >= 0; i--) {
            const { key, value, ref } = tabsRef.current[i];
            const dom = ref.current;
            let endTabOffset = 0;
            if (dom) {
                endTabOffset = vertical ? dom.offsetHeight + getStyle(dom, 'margin-bottom') : dom.offsetWidth + getStyle(dom, 'margin-right');
            }
            hiddenBackOffset += endTabOffset || 0;
            if (totalHiddenOffset > 0) {
                nextMenuList.push({ key, ...value });
            }
            if (hiddenBackOffset >= totalHiddenOffset) {
                break;
            }
        }
        nextMenuList.reverse();
        setMenuList(prevMenuList.concat(nextMenuList));
        setOffset(vertical ? scrollTop : scrollLeft, setNextPrev);
    }, [tabBarPosition]);
    const renderInkBar = () => {
        const inkBarNode = inkBarRef.current;
        if (inkBarNode) {
            const inkBarNodeStyle = inkBarNode.style;
            const activeTab = activeTabRef.current;
            const rootNode = rootRef.current;
            if (activeTab && rootNode) {
                const wrapNode = navRef.current || rootNode;
                const transformSupported = isTransformSupported(inkBarNodeStyle);
                if (!isVertical(tabBarPosition)) {
                    let left = getLeft(activeTab, wrapNode);
                    let width = activeTab.offsetWidth;
                    // If tabNode'width width equal to wrapNode'width when tabBarPosition is top or bottom
                    // It means no css working, then ink bar should not have width until css is loaded
                    if (width === rootNode.offsetWidth) {
                        width = 0;
                    }
                    else if (inkBarStyle && inkBarStyle.width !== undefined) {
                        width = parseFloat(inkBarStyle.width);
                        if (width) {
                            left += (activeTab.offsetWidth - width) / 2;
                        }
                    }
                    // use 3d gpu to optimize render
                    if (transformSupported) {
                        setTransform(inkBarNodeStyle, `translate3d(${left}px,0,0)`);
                        inkBarNodeStyle.width = `${width}px`;
                        inkBarNodeStyle.height = '';
                    }
                    else {
                        inkBarNodeStyle.left = `${left}px`;
                        inkBarNodeStyle.top = '';
                        inkBarNodeStyle.bottom = '';
                        inkBarNodeStyle.right = `${wrapNode.offsetWidth - left - width}px`;
                    }
                }
                else {
                    let top = getTop(activeTab, wrapNode);
                    let height = activeTab.offsetHeight;
                    if (inkBarStyle && inkBarStyle.height !== undefined) {
                        height = parseFloat(inkBarStyle.height);
                        if (height) {
                            top += (activeTab.offsetHeight - height) / 2;
                        }
                    }
                    if (transformSupported) {
                        setTransform(inkBarNodeStyle, `translate3d(0,${top}px,0)`);
                        inkBarNodeStyle.height = `${height}px`;
                        inkBarNodeStyle.width = '';
                    }
                    else {
                        inkBarNodeStyle.left = '';
                        inkBarNodeStyle.right = '';
                        inkBarNodeStyle.top = `${top}px`;
                        inkBarNodeStyle.bottom = `${wrapNode.offsetHeight - top - height}px`;
                    }
                }
            }
            inkBarNodeStyle.visibility = activeTab ? 'visible' : 'hidden';
        }
    };
    useLayoutEffect(() => {
        renderInkBar();
    });
    useEffect(() => {
        handleScrollEvent({ target: { scrollLeft: 0, scrollTop: 0 } });
        const debouncedScroll = debounce(e => {
            handleScrollEvent(e);
        }, 200);
        const scrollEvent = new EventManager(navScrollRef.current);
        scrollEvent.addEventListener('scroll', debouncedScroll);
        return () => {
            scrollEvent.removeEventListener('scroll', debouncedScroll);
            debouncedScroll.cancel();
        };
    }, [tabBarPosition]);
    useLayoutEffect(() => {
        const currentNextPrev = {
            prev,
            next,
        };
        const nextPrev = setNextPrev();
        // wait next, prev show hide
        /* eslint react/no-did-update-set-state:0 */
        if (isNextPrevShown(currentNextPrev) !== isNextPrevShown(nextPrev)) {
            scrollToActiveTab();
        }
        else if (activeKey !== prevActiveKey) {
            setActiveKey(activeKey);
            // can not use props.activeKey
            scrollToActiveTab();
        }
    }, [setNextPrev, isNextPrevShown, prev, next, activeKey, prevActiveKey]);
    useEffect(() => {
        const debouncedResize = debounce(() => {
            setNextPrev();
            scrollToActiveTab();
        }, 200);
        resizeEvent.addEventListener('resize', debouncedResize);
        return () => {
            resizeEvent.removeEventListener('resize', debouncedResize);
            debouncedResize.cancel();
        };
    }, [setNextPrev, scrollToActiveTab, resizeEvent]);
    // 内容变化判断是否显示更多
    useEffect(() => {
        setNextPrev();
    }, [getContent]);
    const inkBarNode = getInkBarNode();
    const tabs = getTabs();
    const groupNode = getGroupNode();
    const scrollbarNode = getScrollBarNode([inkBarNode, tabs]);
    return (React.createElement("div", Object.assign({ role: "tablist", className: classnames(`${prefixCls}-bar`, { [`${prefixCls}-bar-with-groups`]: groupNode }, className), tabIndex: 0, ref: rootRef, onKeyDown: handleKeyDown, style: style }, getDataAttr(restProps)),
        React.createElement("div", { className: `${prefixCls}-bar-inner` },
            groupNode,
            groupNode && React.createElement("div", { className: `${prefixCls}-bar-divider` }),
            getContent(scrollbarNode))));
};
TabBar.displayName = 'TabBar';
TabBar.defaultProps = {
    inkBarAnimated: true,
    scrollAnimated: true,
};
export default TabBar;
